import { Spin } from 'antd';
import React, { FC, PropsWithChildren, useCallback, useEffect } from 'react';
import { Outlet, Route, Routes } from 'react-router-dom';

import { LoadingLayout } from 'components/application/layouts/loading-layout';
import { PrivateLayoutWrapper } from 'components/application/layouts/private-layout/PrivateLayoutContainer';
import { useAuthState } from 'components/authentication/context/authContext';
import { userProfileSelector } from 'components/authentication/context/authSelectors';
import { RbacRoleKeys } from 'constants/rbac';

import OnboardingSetup from './components/OnboardingSetup';
import OnboardingSummary from './components/OnboardingSummary';
import { OnboardingProvider, useOnboardingDispatch, useOnboardingState } from './context/onboardingContext';
import { setStep, Step } from './context/onboardingReducer';
import { flowIdSelector, onboardingSelector, stepSelector } from './context/onboardingSelectors';
import {
  completeFlow,
  createBranchRegistrationWorkflow,
  deleteBranchRegistrationWorkflow,
  excludeBranchesFromRegistrationWorkflow,
  fetchOnboarding,
  includeBranchesToRegistrationWorkflow,
  initializeOnboarding,
  shareFlow,
  updateBranchRegistrationWorkflowCredentials,
} from './context/onboardingThunks';
import { OnboardingLayout } from './layout/OnboardingLayout';

export const OnboardingContainer: FC<PropsWithChildren<{}>> = ({ children }) => {
  const state = useOnboardingState();
  const dispatch = useOnboardingDispatch();
  const flowId = flowIdSelector(state);
  const onboarding = onboardingSelector(state);
  const step = stepSelector(state);
  const authState = useAuthState();
  const userProfile = userProfileSelector(authState) ?? null;
  const userIsOrganizationAdmin = userProfile?.rbac_role_id === RbacRoleKeys.OrganizationAdmin;
  useEffect(() => {
    dispatch(initializeOnboarding(userProfile));
  }, [dispatch, userProfile]);
  const onboardingIsFound = onboarding != null;
  const onboardingIsNotFound = onboarding === null;
  const onboardingIsCompleted = step === null;
  useEffect(() => {
    const event = 'visibilitychange';
    const handleEvent = () => {
      if (document.visibilityState === 'visible' && onboardingIsFound && !onboardingIsCompleted) {
        dispatch(fetchOnboarding());
      }
    };
    document.addEventListener(event, handleEvent);
    return () => document.removeEventListener(event, handleEvent);
  }, [dispatch, onboardingIsFound, onboardingIsCompleted]);
  useEffect(() => {
    const minute = 60 * 1000;
    const intervalId = setInterval(() => {
      if (!onboardingIsFound || onboardingIsCompleted) {
        return;
      }
      if (Date.now() - minute < new Date(onboarding.updatedAt).getTime()) {
        return;
      }
      dispatch(fetchOnboarding());
    }, minute);
    return () => clearInterval(intervalId);
  }, [dispatch, onboardingIsFound, onboardingIsCompleted, onboarding?.updatedAt]);
  useEffect(() => {
    if (!userProfile && (onboardingIsNotFound || onboardingIsCompleted)) {
      window.location.href = '/';
    }
  }, [userProfile, onboardingIsNotFound, onboardingIsCompleted]);
  const handleShareFlow = useCallback(
    async (collaboratorEmail?: string) => {
      await dispatch(
        shareFlow(collaboratorEmail, {
          errorNotification: 'Failed to share onboarding',
          successNotification: collaboratorEmail ? 'Onboarding is shared successfully' : undefined,
        }),
      );
    },
    [dispatch],
  );
  const handleExcludeBranchFromRegistrationWorkflow = useCallback(
    async (id: string, branch: string) => {
      await dispatch(
        excludeBranchesFromRegistrationWorkflow(
          { id, branches: [branch] },
          {
            errorNotification: 'Failed to exclude credentials',
          },
        ),
      );
    },
    [dispatch],
  );
  const handleIncludeBranchToRegistrationWorkflow = useCallback(
    async (id: string, branch: string) => {
      await dispatch(
        includeBranchesToRegistrationWorkflow(
          { id, branches: [branch] },
          {
            errorNotification: 'Failed to include credentials',
          },
        ),
      );
    },
    [dispatch],
  );
  const Layout = userProfile ? <PrivateLayoutWrapper showMenu={false} isMobileAdaptive /> : <Outlet />;
  if (onboarding === undefined || step === undefined) {
    return (
      <Routes>
        <Route element={Layout}>
          <Route
            path="*"
            element={(
              <LoadingLayout>
                <Spin spinning tip="Initializing..." />
              </LoadingLayout>
            )}
          />
        </Route>
      </Routes>
    );
  }
  if (onboarding !== null && step === Step.Setup) {
    return (
      <Routes>
        <Route element={Layout}>
          <Route element={<OnboardingLayout />}>
            <Route
              path="*"
              element={(
                <OnboardingSetup
                  userIsOrganizationAdmin={userIsOrganizationAdmin}
                  flowId={flowId}
                  onboarding={onboarding}
                  onShareFlow={handleShareFlow}
                  onCreateBranchRegistrationWorkflow={async ({ portal, login, password }) => dispatch(
                    createBranchRegistrationWorkflow(
                      {
                        portal,
                        login,
                        password,
                      },
                      {
                        errorNotification: 'Failed to create credentials',
                        errorWaitingForBranchesSearchCompletionNotification: 'Branches search failed',
                      },
                    ),
                  )}
                  onUpdateBranchRegistrationWorkflowCredentials={async ({ id, portal, login, password }) => dispatch(
                    updateBranchRegistrationWorkflowCredentials(
                      {
                        id,
                        portal,
                        login,
                        password,
                      },
                      {
                        errorNotification: 'Failed to update credentials',
                        errorWaitingForBranchesSearchCompletionNotification: 'Branches search failed',
                      },
                    ),
                  )}
                  onExcludeBranchFromRegistrationWorkflow={handleExcludeBranchFromRegistrationWorkflow}
                  onIncludeBranchToRegistrationWorkflow={handleIncludeBranchToRegistrationWorkflow}
                  onRemoveBranchRegistrationWorkflow={async (id) => dispatch(
                    deleteBranchRegistrationWorkflow(
                      {
                        id,
                      },
                      {
                        errorNotification: 'Failed to remove credentials',
                      },
                    ),
                  )}
                  onNext={() => dispatch(setStep(Step.Summary))}
                />
              )}
            />
          </Route>
        </Route>
      </Routes>
    );
  }
  if (onboarding !== null && step === Step.Summary) {
    return (
      <Routes>
        <Route element={Layout}>
          <Route element={<OnboardingLayout />}>
            <Route
              path="*"
              element={(
                <OnboardingSummary
                  userIsOrganizationAdmin={userIsOrganizationAdmin}
                  flowId={flowId}
                  onboarding={onboarding}
                  onExcludeBranchFromRegistrationWorkflow={handleExcludeBranchFromRegistrationWorkflow}
                  onIncludeBranchToRegistrationWorkflow={handleIncludeBranchToRegistrationWorkflow}
                  onShareFlow={handleShareFlow}
                  onBack={() => dispatch(setStep(Step.Setup))}
                  onComplete={async () => dispatch(
                    completeFlow({
                      errorNotification: 'Failed to complete onboarding',
                    }),
                  )}
                />
              )}
            />
          </Route>
        </Route>
      </Routes>
    );
  }
  return <Routes>{children}</Routes>;
};

interface WrapperProps {
  children?: React.ReactNode;
}

export const OnboardingWrapper: FC<WrapperProps> = ({ children }: WrapperProps) => (
  <OnboardingProvider>
    <OnboardingContainer>{children}</OnboardingContainer>
  </OnboardingProvider>
);

export default OnboardingWrapper;
